import { useEffect, useRef, useCallback } from 'react';
import { createFractalWorker } from './createFractalWorker';
import { planPan, copyPanPixels } from './fractalPixels';
import { isAnalyticZeroTile } from './cpuInteriorCertificate';
import { fullConjugateRowMap, fillConjugateRows } from './fractalConjugateRows';
import { createCpuRenderSnapshot } from './fractalCPUProgressive';
import {
  finalizeBuddhabrotPixels,
  getBuddhabrotSampleGrid,
  isBuddhabrotMode,
} from './fractal_math';

const MIN_PIXELS_FOR_WORKER = 80000;
// At most 4096 pixels of obsolete work per busy worker before cancellation
// takes effect. No worker receives another message until its tile finishes.
const TILE_WIDTH = 128;
const TILE_HEIGHT = 32;
// Progressive full jobs leave worker slots available often enough for the
// latest-camera anchor channel; a full job still has a finite completion path.
const PROGRESSIVE_TILE_WIDTH = 64;
const PROGRESSIVE_TILE_HEIGHT = 8;
const IDLE_POOL_TIMEOUT_MS = 3000;
let workers = [];
let requestId = 0;
let workerFailed = false;
let pumping = false;
let idleTimer = null;
const owners = new Set();
let readyOwners = [];

function canUseWorkers() {
  if (typeof Worker === 'undefined') return false;
  return typeof window === 'undefined' ||
    (window.location.protocol !== 'file:' && window.location.origin !== 'null');
}

function abortError(message = 'Render superseded') {
  const error = new Error(message);
  error.name = 'AbortError';
  return error;
}

function rejectJob(job, error) {
  if (!job || job.settled) return;
  job.settled = true;
  job.reject(error);
  // Release incomplete images immediately; in-flight tiles are discarded.
  job.pixels = null;
  job.combinedHist = null;
  job.conjugateMap = null;
  if (job.params && job.type === 'renderExactPan') job.params.sourcePixels = null;
  job.cleanX = null;
  job.cleanY = null;
  job.onTile = null;
  job.regions = [];
  job.tiles = [];
}

function cancelOwner(owner, error) {
  if (!owner) return;
  rejectJob(owner.pending, error);
  owner.pending = null;
  rejectJob(owner.active, error);
  if (owner.active?.inFlight === 0) owner.active = null;
  readyOwners = readyOwners.filter(candidate => candidate !== owner);
}

function clearIdleTimer() {
  if (idleTimer !== null) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

function isPoolIdle() {
  if (readyOwners.length > 0 || workers.some(slot => slot.task !== null)) return false;
  for (const owner of owners) {
    if (owner.active || owner.pending) return false;
  }
  return true;
}

function scheduleIdleStop() {
  clearIdleTimer();
  if (workers.length === 0 || !isPoolIdle()) return;
  idleTimer = setTimeout(() => {
    idleTimer = null;
    if (workers.length > 0 && isPoolIdle()) {
      stopPool(new Error('Idle workers parked'));
    }
  }, IDLE_POOL_TIMEOUT_MS);
  idleTimer?.unref?.();
}

function stopPool(error) {
  clearIdleTimer();
  workers.forEach(slot => {
    slot.task = null;
    slot.worker.terminate();
  });
  workers = [];
  owners.forEach(owner => {
    cancelOwner(owner, error);
    owner.active = null;
  });
  readyOwners = [];
}

function handleWorkerError() {
  workerFailed = true;
  stopPool(new Error('Fractal worker failed'));
}

function ensurePool() {
  clearIdleTimer();
  if (workers.length > 0) return;
  if (workerFailed || !canUseWorkers()) throw new Error('Workers unavailable');
  try {
    const size = Math.min(navigator.hardwareConcurrency || 4, 8);
    for (let i = 0; i < size; i++) {
      const slot = { worker: createFractalWorker(), task: null };
      workers.push(slot);
      slot.worker.onmessage = ({ data: { id, type, data, error } }) => {
        if (!workers.includes(slot)) return;
        completeTile(slot, id, data, type === 'error' ? new Error(error) : null);
      };
      slot.worker.onerror = () => { if (workers.includes(slot)) handleWorkerError(); };
      slot.worker.onmessageerror = () => { if (workers.includes(slot)) handleWorkerError(); };
    }
  } catch (error) {
    workerFailed = true;
    stopPool(error);
    throw error;
  }
}

function ready(owner) {
  const job = owner.active;
  if (job && !job.settled && job.nextTile < job.tiles.length && !readyOwners.includes(owner)) {
    readyOwners.push(owner);
  }
}

function startPending(owner) {
  if (owner.active || !owner.pending) return;
  const job = owner.pending;
  owner.pending = null;
  owner.active = job;
  const { canvasWidth: width, canvasHeight: height } = job.params;
  try {
    if (job.type === 'renderFull' && isBuddhabrotMode(job.params.colorParams?.mode)) {
      const { samplesX, samplesY } = getBuddhabrotSampleGrid(width, height);
      let activeOtherOwners = 0;
      for (const candidate of owners) {
        if (candidate !== owner && (
          (candidate.active && !candidate.active.settled) ||
          (candidate.pending && !candidate.pending.settled)
        )) {
          activeOtherOwners++;
        }
      }
      job.tiles = [];
      job.combinedHist = null;
      if (activeOtherOwners > 0 || workers.length <= 1 || samplesY <= 1) {
        job.buddhabrotMode = 'full';
        job.pixels = null;
        job.tiles.push({
          kind: 'buddhabrotFull',
          samplesX,
          samplesY,
        });
      } else {
        job.buddhabrotMode = 'slices';
        job.pixels = null;
        const sliceCount = Math.max(1, Math.min(4, workers.length, samplesY));
        const rowsPerWorker = Math.ceil(samplesY / sliceCount);
        for (let startSampleY = 0; startSampleY < samplesY; startSampleY += rowsPerWorker) {
          job.tiles.push({
            kind: 'buddhabrotSlice',
            samplesX,
            samplesY,
            startSampleY,
            sampleRegionHeight: Math.min(rowsPerWorker, samplesY - startSampleY),
          });
        }
      }
      finishJob(job);
      ready(owner);
      return;
    }
    const exact = job.type === 'renderExactPan';
    const plan = job.type === 'renderPan' ? planPan(job.params) : {
      regions: [job.type === 'renderTile' ? job.params.tile : { x: 0, y: 0, width, height }]
    };
    if (job.type === 'renderTile') {
      const tile = job.params.tile;
      if (!tile || ![tile.x, tile.y, tile.width, tile.height].every(Number.isInteger) ||
          tile.x < 0 || tile.y < 0 || tile.width < 1 || tile.height < 1 ||
          tile.x + tile.width > width || tile.y + tile.height > height ||
          tile.width * tile.height > TILE_WIDTH * TILE_HEIGHT) {
        throw new Error('Invalid CPU tile bounds or work budget');
      }
    }
    job.pixelOffsetX = plan.pixelOffsetX;
    job.pixelOffsetY = plan.pixelOffsetY;
    job.pixels = job.type === 'renderFull' || exact
      ? new Uint8ClampedArray(width * height * 4) : null;
    if (exact) {
      const { sourcePixels, dx, dy, cleanX, cleanY } = job.params;
      if (!(sourcePixels instanceof Uint8ClampedArray) || sourcePixels.length !== width * height * 4 ||
          !(cleanX instanceof Uint8Array) || cleanX.length !== width ||
          !(cleanY instanceof Uint8Array) || cleanY.length !== height) {
        throw new Error('Invalid exact-pan source or coordinate masks');
      }
      job.cleanX = cleanX;
      job.cleanY = cleanY;
      copyPanPixels(sourcePixels, job.pixels, width, height, dx, dy);
    }
    job.tiles = [];
    const certifiedTiles = [];
    job.dirtyPixels = 0;
    for (const region of plan.regions) {
      const tileWidth = job.type === 'renderTile' ? region.width
        : job.onTile ? PROGRESSIVE_TILE_WIDTH : TILE_WIDTH;
      const tileHeight = job.type === 'renderTile' ? region.height
        : job.onTile ? PROGRESSIVE_TILE_HEIGHT : TILE_HEIGHT;
      for (let y = region.y; y < region.y + region.height; y += tileHeight) {
        for (let x = region.x; x < region.x + region.width; x += tileWidth) {
          const tile = { x, y, width: Math.min(tileWidth, region.x + region.width - x),
            height: Math.min(tileHeight, region.y + region.height - y) };
          if (exact) {
            let cleanColumns = 0, cleanRows = 0;
            for (let j = x; j < x + tile.width; j++) cleanColumns += job.cleanX[j] === 1;
            for (let i = y; i < y + tile.height; i++) cleanRows += job.cleanY[i] === 1;
            tile.dirtyCount = tile.width * tile.height - cleanColumns * cleanRows;
            if (!tile.dirtyCount) continue;
            job.dirtyPixels += tile.dirtyCount;
          }
          // The full image starts transparent. Certified interior tiles need
          // no recurrence, worker message, transfer, or row copy.
          if (job.skipInteriorDispatch &&
              isAnalyticZeroTile({ canvasWidth: width, canvasHeight: height,
                xRange: job.params.newXRange || job.params.xRange,
                yRange: job.params.newYRange || job.params.yRange,
                startX: tile.x, startY: tile.y,
                regionWidth: tile.width, regionHeight: tile.height })) {
            if (job.onTile) certifiedTiles.push(tile);
            continue;
          }
          job.tiles.push(tile);
        }
      }
    }
    for (const tile of certifiedTiles) {
      if (job.settled) break;
      emitAcceptedFullTile(job, tile, new Uint8ClampedArray(tile.width * tile.height * 4));
    }
  } catch (error) {
    rejectJob(job, error);
  }
  finishJob(job);
  ready(owner);
}

function finishJob(job) {
  if (job.inFlight !== 0 || (!job.settled && job.nextTile < job.tiles.length)) return;
  if (!job.settled && job.type === 'renderFull' && job.buddhabrotMode === 'slices') {
    try {
      const { canvasWidth: width, canvasHeight: height } = job.params;
      job.pixels = finalizeBuddhabrotPixels(
        job.combinedHist || new Float32Array(width * height * 4),
        width,
        height,
      );
      job.combinedHist = null;
    } catch (error) {
      rejectJob(job, error);
    }
  }
  if (!job.settled && job.type === 'renderFull' && job.conjugateMap) {
    try {
      // Progressive publication already installed reflected rows a small
      // region at a time. Avoid an extra full-half-frame copy on completion.
      if (!job.onTile) {
        fillConjugateRows(job.pixels, job.params.canvasWidth, job.conjugateMap.partners);
      }
      job.conjugateMap = null;
    } catch (error) {
      rejectJob(job, error);
    }
  }
  if (!job.settled) {
    job.settled = true;
    job.resolve(job.type === 'renderExactPan' ? {
      pixels: job.pixels, workerTiles: job.tiles.length, computedPixels: job.dirtyPixels
    } : job.type === 'renderFull' ? job.pixels : job.type === 'renderTile'
      ? job.regions[0] : {
      pixelOffsetX: job.pixelOffsetX, pixelOffsetY: job.pixelOffsetY, regions: job.regions
    });
  }
  // A local tile callback can synchronously cancel or replace its owner. Do
  // not retire a replacement job while unwinding the older callback.
  if (job.owner.active === job) {
    job.owner.active = null;
    startPending(job.owner);
  }
  if (isPoolIdle()) scheduleIdleStop();
}

// Only computed rows may be installed. Workers leave certified conjugate
// rows empty until their original row arrives, which can be in another tile.
// Publish those original rows and their exact reflected copies together.
function emitAcceptedFullTile(job, tile, pixels) {
  if (!job.onTile || job.settled) return;
  const partners = job.conjugateMap?.partners;
  if (!partners) {
    job.onTile({ ...tile, pixels, snapshot: job.snapshot });
    return;
  }
  const regions = [];
  let row = 0;
  while (row < tile.height) {
    if (partners[tile.y + row] >= 0) { row++; continue; }
    const startRow = row;
    while (row < tile.height && partners[tile.y + row] < 0) row++;
    regions.push({ x: tile.x, y: tile.y + startRow, width: tile.width,
      height: row - startRow,
      pixels: pixels.slice(startRow * tile.width * 4, row * tile.width * 4),
      snapshot: job.snapshot });
  }
  // A reflected block has reverse row order. It still uses the original x
  // pixel centers, and its target rows have been certified as exact conjugates.
  let sourceRow = tile.height - 1;
  while (sourceRow >= 0) {
    const originalRow = tile.y + sourceRow;
    const targetRow = job.params.canvasHeight - 1 - originalRow;
    if (partners[targetRow] !== originalRow) { sourceRow--; continue; }
    const lastSourceRow = sourceRow;
    while (sourceRow >= 0 &&
        partners[job.params.canvasHeight - 1 - tile.y - sourceRow] === tile.y + sourceRow) {
      sourceRow--;
    }
    const height = lastSourceRow - sourceRow;
    const mirrored = new Uint8ClampedArray(tile.width * height * 4);
    for (let i = 0; i < height; i++) {
      const sourceStart = (lastSourceRow - i) * tile.width * 4;
      mirrored.set(pixels.subarray(sourceStart, sourceStart + tile.width * 4),
        i * tile.width * 4);
      job.pixels.set(mirrored.subarray(i * tile.width * 4, (i + 1) * tile.width * 4),
        ((targetRow + i) * job.params.canvasWidth + tile.x) * 4);
    }
    regions.push({ x: tile.x, y: targetRow, width: tile.width, height,
      pixels: mirrored, snapshot: job.snapshot });
  }
  for (const region of regions) {
    if (job.settled) break;
    job.onTile(region);
  }
}

function completeTile(slot, id, pixels, error) {
  if (slot.task?.id !== id) return;
  const { job, tile } = slot.task;
  slot.task = null;
  job.inFlight--;
  if (!job.settled) {
    try {
      if (error) throw error;
      if (tile.kind === 'buddhabrotSlice') {
        const expectedLen = job.params.canvasWidth * job.params.canvasHeight * 4;
        if (!(pixels instanceof Float32Array) || pixels.length !== expectedLen) {
          throw new Error('Invalid worker buddhabrot histogram');
        }
        if (!job.combinedHist) {
          job.combinedHist = pixels;
        } else {
          for (let i = 0; i < expectedLen; i++) {
            job.combinedHist[i] += pixels[i];
          }
        }
      } else if (tile.kind === 'buddhabrotFull') {
        const expectedBytes = job.params.canvasWidth * job.params.canvasHeight * 4;
        if (!(pixels instanceof Uint8ClampedArray) || pixels.length !== expectedBytes) {
          throw new Error('Invalid worker render stripe');
        }
        job.pixels = pixels;
      } else {
      const expectedLength = job.type === 'renderExactPan'
        ? tile.dirtyCount * 4 : tile.width * tile.height * 4;
      if (pixels.length !== expectedLength) throw new Error('Invalid worker tile');
      if (job.type === 'renderExactPan') {
        const source32 = new Uint32Array(pixels.buffer, pixels.byteOffset, pixels.length / 4);
        const target32 = new Uint32Array(job.pixels.buffer);
        let sourceIndex = 0;
        for (let row = 0; row < tile.height; row++) {
          const cleanRow = job.cleanY[tile.y + row] === 1;
          for (let col = 0; col < tile.width; col++) {
            if (cleanRow && job.cleanX[tile.x + col] === 1) continue;
            target32[(tile.y + row) * job.params.canvasWidth + tile.x + col] = source32[sourceIndex++];
          }
        }
        if (sourceIndex !== tile.dirtyCount) throw new Error('Exact-pan sparse output count mismatch');
      } else if (job.type === 'renderFull') {
        for (let row = 0; row < tile.height; row++) {
          if (job.onTile && (job.conjugateMap?.partners[tile.y + row] ?? -1) >= 0) continue;
          const start = row * tile.width * 4;
          job.pixels.set(pixels.subarray(start, start + tile.width * 4),
            ((tile.y + row) * job.params.canvasWidth + tile.x) * 4);
        }
        emitAcceptedFullTile(job, tile, pixels);
      } else {
        job.regions.push({ ...tile, pixels,
          ...(job.type === 'renderTile' ? { snapshot: job.snapshot } : {}) });
      }
      }
    } catch (failure) {
      rejectJob(job, failure);
    }
  }
  finishJob(job);
  pump();
}

function pump() {
  if (pumping) return;
  pumping = true;
  try {
    for (const slot of workers) {
      while (!slot.task && readyOwners.length) {
        // Round-robin between canvases so a large render cannot monopolize
        // every newly available worker.
        const owner = readyOwners.shift();
        const job = owner.active;
        if (!job || job.settled || job.nextTile >= job.tiles.length) continue;
        const tile = job.tiles[job.nextTile++];
        const id = ++requestId;
        slot.task = { id, job, tile };
        job.inFlight++;
        ready(owner);
        try {
          if (tile.kind === 'buddhabrotSlice') {
            slot.worker.postMessage({
              id,
              type: 'renderBuddhabrotSlice',
              params: {
                canvasWidth: job.params.canvasWidth,
                canvasHeight: job.params.canvasHeight,
                xRange: job.params.newXRange || job.params.xRange,
                yRange: job.params.newYRange || job.params.yRange,
                colorParams: job.params.colorParams,
                ...(job.params.motionPreview ? { motionPreview: true } : {}),
                samplesX: tile.samplesX,
                samplesY: tile.samplesY,
                startSampleY: tile.startSampleY,
                sampleRegionHeight: tile.sampleRegionHeight,
              },
            });
            continue;
          }
          if (tile.kind === 'buddhabrotFull') {
            slot.worker.postMessage({
              id,
              type: 'renderFull',
              params: {
                canvasWidth: job.params.canvasWidth,
                canvasHeight: job.params.canvasHeight,
                xRange: job.params.newXRange || job.params.xRange,
                yRange: job.params.newYRange || job.params.yRange,
                colorParams: job.params.colorParams,
                ...(job.params.motionPreview ? { motionPreview: true } : {}),
                samplesX: tile.samplesX,
                samplesY: tile.samplesY,
              },
            });
            continue;
          }
          const exact = job.type === 'renderExactPan';
          const taskParams = {
            canvasWidth: job.params.canvasWidth, canvasHeight: job.params.canvasHeight,
            xRange: job.params.newXRange || job.params.xRange,
            yRange: job.params.newYRange || job.params.yRange,
            colorParams: job.params.colorParams,
            startX: tile.x, startY: tile.y, regionWidth: tile.width, regionHeight: tile.height,
            // A subarray structured clone can copy its entire backing mask
            // per tile. Send only the tile-sized mask bytes instead.
            ...(exact ? { cleanX: job.cleanX.slice(tile.x, tile.x + tile.width),
              cleanY: job.cleanY.slice(tile.y, tile.y + tile.height) } : {})
          };
          if (job.conjugateMap) taskParams.conjugateFullRows = true;
          slot.worker.postMessage({ id, type: exact ? 'renderSparse' : 'renderRegion',
            params: taskParams });
        } catch (error) {
          completeTile(slot, id, null, error);
        }
      }
    }
  } finally {
    pumping = false;
  }
}

// Hold one numerical grid for every omitted tile and every remaining worker
// message, including ordinary/count jobs retained while input changes.
function snapshotEligibleFullParams(params, enabled) {
  if (!enabled) return params;
  const xRange = params.newXRange || params.xRange;
  const yRange = params.newYRange || params.yRange;
  if (!Array.isArray(xRange) || !Array.isArray(yRange) ||
      xRange.length !== 2 || yRange.length !== 2) return params;
  return { ...params, xRange: [...xRange], yRange: [...yRange],
    newXRange: null, newYRange: null,
    colorParams: params.colorParams && typeof params.colorParams === 'object'
      ? { ...params.colorParams } : params.colorParams,
    ...(params.tile ? { tile: { ...params.tile } } : {}) };
}

function requestRender(owner, type, params) {
  if (!owner) return Promise.reject(abortError('Canvas unmounted'));
  return new Promise((resolve, reject) => {
    ensurePool();
    cancelOwner(owner, abortError());
    const skipInteriorDispatch = type === 'renderFull' &&
      params?.cpuInteriorDispatch === true && params.colorParams?.useMu !== false;
    const numericalYRange = params?.newYRange || params?.yRange;
    // Count and selected period-3 jobs cannot use real-axis row reuse. This
    // cheap guard avoids even calling the row helper on their common grids.
    const conjugateMap = type === 'renderFull' &&
      params?.colorParams?.useMu !== false &&
      Array.isArray(numericalYRange) && numericalYRange[0] < 0 &&
      numericalYRange[1] > 0
      ? fullConjugateRowMap({ ...params,
        xRange: params.newXRange || params.xRange,
        yRange: numericalYRange }) : null;
    const { onTile, ...serializableParams } = params;
    const snapshotParams = snapshotEligibleFullParams(serializableParams,
      type === 'renderFull' || type === 'renderTile');
    owner.pending = { owner, type, params: snapshotParams,
      onTile: type === 'renderFull' && typeof onTile === 'function' ? onTile : null,
      snapshot: type === 'renderFull' || type === 'renderTile'
        ? createCpuRenderSnapshot({ width: snapshotParams.canvasWidth,
          height: snapshotParams.canvasHeight,
          xRange: snapshotParams.newXRange || snapshotParams.xRange,
          yRange: snapshotParams.newYRange || snapshotParams.yRange,
          colorParams: snapshotParams.colorParams,
          cameraRequestedAt: snapshotParams.cameraRequestedAt }) : null,
      skipInteriorDispatch, conjugateMap, resolve, reject, settled: false,
      inFlight: 0, nextTile: 0, tiles: [], regions: [] };
    startPending(owner);
    pump();
  });
}

export function useFractalWorker() {
  const ownerRef = useRef(null);
  useEffect(() => {
    const tileOwner = { active: null, pending: null };
    const owner = { active: null, pending: null, tileOwner };
    ownerRef.current = owner;
    owners.add(owner);
    owners.add(tileOwner);
    return () => {
      ownerRef.current = null;
      cancelOwner(owner, abortError('Canvas unmounted'));
      cancelOwner(tileOwner, abortError('Canvas unmounted'));
      owners.delete(owner);
      owners.delete(tileOwner);
      if (owners.size === 0) {
        stopPool(new Error('Workers terminated'));
        workerFailed = false;
      }
    };
  }, []);

  const renderFull = useCallback(params => requestRender(ownerRef.current, 'renderFull', params), []);
  const renderPan = useCallback(params => requestRender(ownerRef.current, 'renderPan', params), []);
  const renderExactPan = useCallback(params => requestRender(ownerRef.current, 'renderExactPan', params), []);
  const renderTile = useCallback(params => requestRender(ownerRef.current?.tileOwner, 'renderTile', params), []);
  const cancelRender = useCallback(() => {
    cancelOwner(ownerRef.current, abortError());
    cancelOwner(ownerRef.current?.tileOwner, abortError());
    if (isPoolIdle()) scheduleIdleStop();
  }, []);
  const isWorkerAvailable = useCallback(() => !workerFailed && canUseWorkers(), []);
  const shouldUseWorker = useCallback((width, height) => {
    return width * height >= MIN_PIXELS_FOR_WORKER && isWorkerAvailable();
  }, [isWorkerAvailable]);
  return { renderFull, renderPan, renderExactPan, renderTile, cancelRender, isWorkerAvailable, shouldUseWorker };
}

/**
 * Parse a colorFunc to extract serializable parameters for the worker
 * Since functions can't be sent to workers, we need to serialize them
 */
export function serializeColorFunc(colorFunc) {
  if (colorFunc?.colorParams) return { ...colorFunc.colorParams };

  // Get the function source to analyze it
  const funcStr = colorFunc.toString();
  
  // Default values
  const params = {
    r: 255,
    gScale: 7,
    b: 0,
    aScale: 15,
    aFixed: null,
    useMu: true
  };

  // Check if it uses numIterations instead of mu
  if (funcStr.includes('numIterations')) {
    params.useMu = false;
  }

  // Extract multipliers using regex
  // Pattern: mu * N or numIterations * N
  const gScaleMatch = funcStr.match(/(?:mu|numIterations)\s*\*\s*(\d+)/);
  if (gScaleMatch) {
    params.gScale = parseInt(gScaleMatch[1], 10);
  }

  // Look for alpha scale - usually the second multiplier or a fixed value
  // Pattern: [ 255, ... , 0, mu * N ] or [ 255, ..., 0, 255 ]
  const alphaMatches = funcStr.match(/,\s*(?:(\d+)|(?:mu|numIterations)\s*\*\s*(\d+))\s*\]/);
  if (alphaMatches) {
    if (alphaMatches[1]) {
      // Fixed alpha value
      params.aFixed = parseInt(alphaMatches[1], 10);
    } else if (alphaMatches[2]) {
      // Alpha scale
      params.aScale = parseInt(alphaMatches[2], 10);
    }
  }

  return params;
}
